import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import SectionHeader from '@/components/researcher/SectionHeader'
import ActivityHeatmap from './ActivityHeatmap'
import type { LiveEvent } from './monitoringMockData'

type LiveActivityProps = {
  events: LiveEvent[]
  heatmap: number[]
}

/** Live event feed + 24h activity heatmap. */
export default function LiveActivity({ events, heatmap }: LiveActivityProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('monitoring')

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: '18px', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.75 }}
    >
      <SectionHeader
        title={t('live.title')}
        right={
          <Box
            component="span"
            sx={{ display: 'inline-flex', gap: 0.75, alignItems: 'center', fontSize: 11, color: c.success, fontWeight: 600 }}
          >
            <Box
              component="span"
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.success, animation: 'affexy-pulse 1.6s ease-in-out infinite' }}
            />
            {t('live.updateInterval')}
          </Box>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((e, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'center',
              py: 1,
              borderBottom: i < events.length - 1 ? `1px dashed ${c.ink08}` : 'none',
            }}
          >
            <Box
              component="span"
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.coral, animation: 'affexy-pulse 1.6s ease-in-out infinite' }}
            />
            <Typography sx={{ fontSize: 13, color: c.ink, flex: 1 }}>
              <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {e.who}
              </Box>{' '}
              {t(`live.events.${e.kind}`)}
            </Typography>
            <Typography
              sx={{ fontSize: 11.5, color: c.ink48, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              {t(e.unit === 'sec' ? 'live.time.secAgo' : 'live.time.minAgo', { count: e.amount })}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack spacing={1} sx={{ mt: 0.75 }}>
        <Typography
          sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: c.ink48, fontWeight: 700 }}
        >
          {t('live.heatmapTitle')}
        </Typography>
        <ActivityHeatmap vals={heatmap} />
      </Stack>
    </Paper>
  )
}
